'use client';

import { Box, HStack, VStack, css } from '@terra/ui-styled-system';
import { useState, useMemo } from 'react';
import { ArrowDown01Icon, ArrowRight01Icon, Copy01Icon } from '@terra/ui-icons';
import { Text } from '../../../components/styled/text';
import { Badge } from '../../../components/badge';
import { BadgeHttp } from '../../../components/styled/badge-http';
import { IconButton } from '../../../components/icon-button';
import { Tooltip } from '../../../components/tooltip';
import { Collapsible } from '../../../components/collapsible';
import type { HttpMentionData } from '@terra/shared-dto-chat-types';
import { normalizeHttpHeaders } from '@terra/shared-utils';

type HttpMentionCardProps = {
  data: HttpMentionData;
  type: 'request' | 'response';
  /** Whether to show in compact mode (less detail) */
  compact?: boolean;
};
const cardStyles = css({
  borderRadius: 'md',
  borderWidth: '1px',
  borderColor: 'border.muted',
  bg: 'bg.subtle',
  overflow: 'hidden',
  fontSize: 'xs',
});
const headerStyles = css({
  px: 3,
  py: 2,
  bg: 'bg.muted',
  borderBottomWidth: '1px',
  borderColor: 'border.muted',
  cursor: 'pointer',
  _hover: {
    bg: 'bg.hover',
  },
});
const codeBlockStyles = css({
  fontFamily: 'mono',
  fontSize: '2xs',
  bg: 'bg.canvas',
  p: 2,
  borderRadius: 'sm',
  overflow: 'auto',
  maxHeight: '200px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
});
const HEADERS_LABEL = 'Headers';
const BODY_LABEL = 'Body';

/**
 * Format URL for display (show path only if too long)
 */
function formatUrlForDisplay(url: string, maxLength = 50): string {
  if (url.length <= maxLength) return url;

  try {
    const parsed = new URL(url);
    const pathAndQuery = parsed.pathname + parsed.search;

    if (pathAndQuery.length <= maxLength) {
      return `...${pathAndQuery}`;
    }

    return pathAndQuery.slice(0, maxLength - 3) + '...';
  } catch {
    return url.slice(0, maxLength - 3) + '...';
  }
}

/**
 * Pretty print body if it's JSON
 */
function formatBody(body: string | undefined, contentType?: string): string {
  if (!body) return '';
  if (contentType?.includes('application/json')) {
    try {
      return JSON.stringify(JSON.parse(body), null, 2);
    } catch {
      return body;
    }
  }

  return body;
}

export function HttpMentionCard({
  data,
  type,
  compact = false,
}: HttpMentionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const displayHeaders = useMemo(
    () => normalizeHttpHeaders(data.headers),
    [data.headers],
  );
  const headersCount = Object.keys(displayHeaders).length;
  const headersTitle = `${HEADERS_LABEL} (${headersCount})`;
  const moreHeadersLabel = `... and ${headersCount - 3} more`;
  const bodyTitle = data.contentType
    ? `${BODY_LABEL} (${data.contentType.split(';')[0]})`
    : BODY_LABEL;
  const hasBody = !!data.body && data.body.trim().length > 0;
  const formattedBody = useMemo(
    () => formatBody(data.body, data.contentType),
    [data.body, data.contentType],
  );
  const handleCopy = async (text: string, section: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 1500);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleCopyAll = () => {
    handleCopy(data.rawText, 'all');
  };

  return (
    <Box className={cardStyles}>
      {/* Header - always visible */}
      <HStack
        className={headerStyles}
        justifyContent="space-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <HStack gap={2} flex={1} minW={0}>
          {/* Expand/collapse icon */}
          <Box color="fg.muted" flexShrink={0}>
            {isExpanded ? (
              <ArrowDown01Icon size={14} />
            ) : (
              <ArrowRight01Icon size={14} />
            )}
          </Box>

          {/* Type badge */}
          <Badge size="sm" variant="emphasis">
            {type === 'request' ? 'REQ' : 'RES'}
          </Badge>

          {/* Method badge (for requests) */}
          {type === 'request' && (
            <BadgeHttp method={data.method} size="sm" variant="fill">
              {data.method}
            </BadgeHttp>
          )}

          {/* Status badge (for responses) */}
          {type === 'response' && data.statusCode && (
            <BadgeHttp status={data.statusCode} size="sm" variant="fill">
              {data.statusCode} {data.statusText || ''}
            </BadgeHttp>
          )}

          {/* URL/Path */}
          <Text
            fontSize="xs"
            color="fg.default"
            truncate
            flex={1}
            fontFamily="mono"
          >
            {formatUrlForDisplay(data.url, compact ? 30 : 50)}
          </Text>
        </HStack>

        {/* Quick actions */}
        <HStack gap={1} flexShrink={0} onClick={(e) => e.stopPropagation()}>
          <Tooltip title={copiedSection === 'all' ? 'Copied!' : 'Copy all'}>
            <IconButton
              size="xs"
              variant="ghost"
              aria-label="Copy all"
              onClick={handleCopyAll}
            >
              <Copy01Icon size={12} />
            </IconButton>
          </Tooltip>
        </HStack>
      </HStack>

      {/* Expanded content */}
      <Collapsible.Root open={isExpanded}>
        <Collapsible.Content>
          <VStack gap={2} p={3} alignItems="stretch">
            {/* Full URL */}
            {!compact && (
              <Box>
                <HStack justifyContent="space-between" mb={1}>
                  <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                    URL
                  </Text>
                  <Tooltip title="Copy URL">
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label="Copy URL"
                      onClick={() => handleCopy(data.url, 'url')}
                    >
                      <Copy01Icon size={10} />
                    </IconButton>
                  </Tooltip>
                </HStack>
                <Text
                  fontSize="2xs"
                  fontFamily="mono"
                  color="fg.default"
                  wordBreak="break-all"
                >
                  {data.url}
                </Text>
              </Box>
            )}

            {/* Headers section */}
            <Box>
              <HStack justifyContent="space-between" mb={1}>
                <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                  {headersTitle}
                </Text>
                <Tooltip title="Copy headers">
                  <IconButton
                    size="xs"
                    variant="ghost"
                    aria-label="Copy headers"
                    onClick={() =>
                      handleCopy(
                        Object.entries(displayHeaders)
                          .map(([key, value]) => {
                            const headerValue = Array.isArray(value)
                              ? value.join(', ')
                              : value;

                            return `${key}: ${headerValue}`;
                          })
                          .join('\n'),
                        'headers',
                      )
                    }
                  >
                    <Copy01Icon size={10} />
                  </IconButton>
                </Tooltip>
              </HStack>
              <Box className={codeBlockStyles}>
                {Object.entries(displayHeaders)
                  .slice(0, compact ? 3 : undefined)
                  .map(([key, value]) => (
                    <Box key={key}>
                      <Text as="span" color="fg.muted">
                        {key}:
                      </Text>{' '}
                      <Text as="span" color="fg.default">
                        {Array.isArray(value) ? value.join(', ') : value}
                      </Text>
                    </Box>
                  ))}
                {compact && headersCount > 3 && (
                  <Text color="fg.muted" fontStyle="italic">
                    {moreHeadersLabel}
                  </Text>
                )}
              </Box>
            </Box>

            {/* Body section */}
            {hasBody && (
              <Box>
                <HStack justifyContent="space-between" mb={1}>
                  <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                    {bodyTitle}
                  </Text>
                  <Tooltip title="Copy body">
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label="Copy body"
                      onClick={() => handleCopy(data.body || '', 'body')}
                    >
                      <Copy01Icon size={10} />
                    </IconButton>
                  </Tooltip>
                </HStack>
                <Box className={codeBlockStyles}>
                  <Text fontSize="2xs" color="fg.default" whiteSpace="pre-wrap">
                    {compact && formattedBody.length > 200
                      ? formattedBody.slice(0, 200) + '...'
                      : formattedBody}
                  </Text>
                </Box>
              </Box>
            )}
          </VStack>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}
